import { ReactElement, useState, useMemo } from 'react';
import { Typography, MultiSelect, SearchField, VenueIcon } from '@weareopenr/component-library';
import { useVenueCategories } from 'src/hooks/useVenueCategories';
import { useDebounce } from 'src/hooks/useDebounce/useDebounce';
import type { IPublishViewProps } from '../types';
import styles from './PublishView.module.scss';
import {
  venueCategoriesToSelectOptions,
  venuesInSelectedCategories,
} from 'src/utils/venueCategories/mappedVenueCategories';
import { getVenuesWithChannels, IVenueWithChannels } from '../utilities/getVenuesWithChannels/getVenuesWithChannels';
import SelectableListWithControls from 'src/components/SelectableListWithControls/SelectableListWithControls';

const PublishView = ({
  publicationData,
  selectedVenueIds,
  onVenuesChange,
  selectedChannelIds,
  eyebrow = 'Menu Publish',
}: IPublishViewProps): ReactElement => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCategoryIds, setFilteredCategoryIds] = useState<string[]>([]);
  const debouncedSearchTerm = useDebounce(searchTerm);
  const { nestedCategories: nestedVenueCategories = [], data } = useVenueCategories();

  const venuesWithChannels = useMemo(
    () => getVenuesWithChannels(publicationData, selectedChannelIds),
    [publicationData, selectedChannelIds],
  );

  const filteredVenues = useMemo(() => {
    const allowedVenueIds =
      filteredCategoryIds.length > 0
        ? new Set(venuesInSelectedCategories(filteredCategoryIds, data?.categories ?? []))
        : null;

    return venuesWithChannels.filter(({ venue }) => {
      const matchesSearch = debouncedSearchTerm
        ? venue.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        : true;

      if (!allowedVenueIds) {
        return matchesSearch;
      }

      return allowedVenueIds.has(venue.venue_id) && matchesSearch;
    });
  }, [venuesWithChannels, debouncedSearchTerm, filteredCategoryIds, data]);

  const mappedVenueCategories = useMemo(
    () => venueCategoriesToSelectOptions(nestedVenueCategories),
    [nestedVenueCategories],
  );

  const getVenueOptions = (venues: IVenueWithChannels[]) =>
    venues.map(({ venue, channelNames }) => ({
      id: venue.venue_id,
      label: venue.name,
      subtitle: channelNames.join(', '),
    }));

  const allVenueOptions = useMemo(() => getVenueOptions(venuesWithChannels), [venuesWithChannels]);

  const venueOptions = useMemo(() => getVenueOptions(filteredVenues), [filteredVenues]);

  const selectedVenueOptions = useMemo(
    () => allVenueOptions.filter(({ id }) => selectedVenueIds.includes(id)),
    [allVenueOptions, selectedVenueIds],
  );

  return (
    <section className={styles['publish-view']}>
      <Typography weight="bold" size="medium" className={styles['eyebrow']}>
        {eyebrow}
      </Typography>
      <Typography weight="bold" size="xxlarge" className={styles['subheader']}>
        Publish Destinations
      </Typography>

      <SelectableListWithControls
        options={venueOptions}
        selectedOptionsIds={selectedVenueIds}
        onOptionChange={onVenuesChange}
        collapsible={{ selectedOptionsData: selectedVenueOptions }}
        listDetails={{ title: 'Venues:', emptyMessage: 'No venues match your search.' }}
      >
        <SearchField
          isDisabled={false}
          placeholder="Search for a venue"
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <MultiSelect
          label="Categories"
          Icon={VenueIcon}
          options={mappedVenueCategories}
          selected={filteredCategoryIds}
          onChange={setFilteredCategoryIds}
          contentClassName={styles['filter-multiselect-content']}
        />
      </SelectableListWithControls>
    </section>
  );
};

export default PublishView;
